import { bellwetherAuthoredData } from './bellwetherAuthoredData.js';

const actors = { Nia: 'curator', Owen: 'guard', Sana: 'artist' };
const voices = { curator: 'bm_fable', guard: 'af_bella', artist: 'bm_fable' };
const allActors = Object.values(actors);

// The reviewed manuscript expands the existing story; it does not rename actors
// or rewrite its original dialogue. Prior saved/prepared catalog v1 stays separate.
export function buildExpandedBellwether(base) {
  if (base.id !== 'museum-after-hours') throw new Error('Expected the existing Bellwether scenario.');
  const result = structuredClone(base);
  // Reconcile legacy briefing claims with the expanded discovery sequence.
  // Preserve the old scenario literal and all fixed lines as separate history.
  result.characters = result.characters.map(character => ({
    ...character,
    ...(character.id === 'guard' ? {
      secret: 'At 2:13 a.m. you heard one short laugh in the west gallery. You were awake and alone, but you do not know what made the sound.',
    } : character.id === 'artist' ? {
      secret: 'Your grandmother Elena Bell restored paintings at this museum. You want to learn what she found; inspect the hidden mark before identifying whose signature it is.',
    } : {}),
  }));
  const usedAnchors = new Set();
  result.catalogRevision = 2;
  result.duration = 'Full story · 475 cues';
  result.scenes = bellwetherAuthoredData.scenes.map(scene => ({ ...scene, tone: 'Evidence, choices, and discovery' }));
  result.cues = bellwetherAuthoredData.cues.map(authored => {
    const characterId = actors[authored.actor];
    const ending = authored.actor === 'ENDING CHOICE';
    const matches = base.cues.filter(cue =>
      (ending ? cue.type === 'ending' : cue.characterId === characterId && Boolean(characterId))
      && (cue.dialogueText || cue.text) === authored.dialogueText);
    if (matches.length > 1) throw new Error('Ambiguous Bellwether source anchor.');
    const anchor = matches[0];
    if (anchor) usedAnchors.add(anchor.id);
    const common = {
      id: anchor?.id || `bellwether-c${String(authored.number).padStart(3, '0')}`,
      sceneId: authored.sceneId,
      label: anchor?.label || `Cue ${authored.number} · ${authored.actor}`,
      directionText: authored.directionText,
      spokenDirectionText: authored.spokenDirectionText,
      requireAck: true,
    };
    if (characterId) return {
      ...common, type: 'dialogue', visibility: 'character', characterId,
      text: authored.dialogueText, dialogueText: authored.dialogueText, cueVoice: true,
    };
    if (!ending && authored.actor !== 'GROUP PERFORMANCE BEAT') throw new Error('Unknown Bellwether actor.');
    return {
      ...common, type: ending ? 'ending' : 'action', visibility: 'selected',
      characterIds: [...allActors], text: ending ? authored.dialogueText : authored.directionText,
      directorVoice: true, completionMode: 'director',
    };
  });
  if (usedAnchors.size !== base.cues.length) throw new Error('Bellwether expansion lost a source anchor.');
  result.characterVoices = {
    version: 1,
    assignments: Object.fromEntries(allActors.map(id => [
      id, { provider: 'voicebox', nativeVoiceId: voices[id], source: 'manual' },
    ])),
  };
  // Avoid retaining obsolete two-scene briefs; normalization uses the existing
  // actor-brief contract to create context for all ten expanded scenes.
  delete result.actorBriefs;
  result.script = result.cues.map(cue => ({
    type: cue.type === 'dialogue' ? 'dialogue' : 'direction',
    sceneId: cue.sceneId, characterId: cue.characterId,
    speaker: result.characters.find(character => character.id === cue.characterId)?.name,
    text: cue.dialogueText || cue.text,
  }));
  return result;
}
